import { HeroReviews, IconName, TAGButton, TAGSvgIcon } from '@aspendental/shared-components-web';
import MediaSlider from '@aspendental/shared-components-web/lib/components/ProductFeature/MediaSlider/MediaSlider';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import { getTop3ReviewsWithComments } from '@src/util/review-with-comments';
import { ExtendedProvider, IProviderDetailsPage } from './ProviderDetailsPage';
import {
	getProviderEducationDetails,
	getProviderHeroTitle,
	getProviderHireDate,
	getProviderLanguages,
} from './ProviderDetailsPage.helpers';
import {
	getHeroContainerStyles,
	getHeroContentContainerStyles,
	getHeroImageWrapperStyles,
	getHeroLabelsButtonStyles,
	getHeroLabelsItemStyles,
	getHeroLabelsListStyles,
	heroTitleStyles,
} from './ProviderDetailsPage.styles';

interface IHeroLabel {
	label: string;
	icon: IconName;
	href?: string;
	target?: string;
}

export default function ProviderHero({ provider, config, reviews, selectedReviews }: IProviderDetailsPage) {
	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	const providerHeroTheme = config?.featureFlags?.providerHeroTheme || 'dark';
	const withLeadingProviderAssociations = config?.featureFlags?.withLeadingProviderAssociations || false;
	const mainLocation = provider.providerLocations?.find(({ isMain }) => isMain);
	const primaryFacility = mainLocation?.facility;
	const mainAddress = mainLocation?.facility?.location?.address1 || '';
	const mainCity = mainLocation?.facility?.location?.city || '';
	const mainState = mainLocation?.facility?.location?.stateCode || '';
	const mainZipCode = mainLocation?.facility?.location?.zipCode || '';
	const education = getProviderEducationDetails(provider);
	const hireDate = getProviderHireDate(provider);
	const languages = getProviderLanguages(provider);
	const languagesLabelsList: IHeroLabel[] = languages ? [{ label: languages, icon: 'Language' }] : [];
	const associationLabelsList: IHeroLabel[] =
		provider.associations?.map((association) => ({
			label: association ? association.trim() : '',
			icon: 'Verified',
		})) || [];

	const top3ReviewsWithComments = getTop3ReviewsWithComments(selectedReviews, reviews);

	const labelList: IHeroLabel[] = [
		...(mainLocation
			? [
					{
						label: `${mainAddress} ${mainCity}, ${mainState}`,
						icon: 'LocationOn' as IconName,
						href: config.googleMapBusinessQuery.replace(
							/\[address\]/gi,
							`${mainAddress}, ${mainCity}, ${mainState} ${mainZipCode}`
						),
						target: '_blank',
					},
			  ]
			: []),
		...(education
			? [
					{
						label: education,
						icon: 'MenuBookIcon' as IconName,
					},
			  ]
			: []),
		...(withLeadingProviderAssociations ? associationLabelsList : []),
		...(hireDate
			? [
					{
						label: `With ${config.name} since ${hireDate}`,
						icon: 'Verified' as IconName,
					},
			  ]
			: []),
		...languagesLabelsList,
		...(!withLeadingProviderAssociations ? associationLabelsList : []),
	];
	const textColor = providerHeroTheme === 'light' ? 'text.primary' : 'text.light';
	const hasProviderPhotos = !!provider.providerImages.length;

	return (
		<Box
			data-testid="provider_hero_container"
			sx={getHeroContainerStyles(providerHeroTheme, theme, hasProviderPhotos)}
		>
			<Box
				data-testid="provider_hero_content_container"
				sx={getHeroContentContainerStyles(theme, hasProviderPhotos)}
			>
				<Typography
					color={textColor}
					component="h1"
					variant={isMobileScreen ? 'heroMedium' : 'heroLarge'}
					textAlign={{ xs: 'center', md: 'left' }}
					sx={heroTitleStyles}
					data-testid="provider_hero_title"
				>
					{getProviderHeroTitle(provider)}
				</Typography>
				{config?.name === 'Aspen Dental' && provider?.quote && (
					<Typography color={textColor} variant="header4" data-testid="provider_hero_quote">
						&ldquo;{provider.quote}&rdquo;
					</Typography>
				)}
				<Box
					sx={getHeroLabelsListStyles(hasProviderPhotos)}
					flexWrap="wrap"
					data-testid="provider_hero_content_section"
				>
					{labelList.map(({ label, icon, href, target }) => {
						if (label) {
							if (href) {
								return (
									<TAGButton
										key={label}
										size="small"
										variant="text"
										startIcon={<TAGSvgIcon icon={icon} size={20} />}
										sx={getHeroLabelsButtonStyles(textColor)}
										href={href}
										target={target}
									>
										{label}
									</TAGButton>
								);
							} else {
								return (
									<Box sx={getHeroLabelsItemStyles(textColor)} key={label}>
										<TAGSvgIcon icon={icon} size={20} />
										<Typography color={textColor} variant="buttonLabel" width={'100%'}>
											{label}
										</Typography>
									</Box>
								);
							}
						}
					})}
				</Box>
				{reviews && (reviews.reviewCount ?? 0) >= 5 && (
					<Box pt={1}>
						<HeroReviews
							reviews={reviews}
							top3Reviews={top3ReviewsWithComments}
							facilityName={getProviderHeroTitle(provider) || ''}
						/>
					</Box>
				)}
			</Box>
			{provider.providerImages.length > 0 && (
				<Box sx={getHeroImageWrapperStyles} data-testid="provider_hero_images_slider">
					<MediaSlider
						fullHeight
						imageSizes="(max-width: 900px) 100vw, 900px"
						contentsCollection={{
							items: (provider as ExtendedProvider).providerImages.map(({ url }, index) => ({
								_id: '',
								__typename: 'FocalPointImage' as const,
								image: {
									_id: '',
									url,
									linkedFrom: undefined,
									contentfulMetadata: {
										tags: [],
										concepts: [],
									},
									sys: {
										environmentId: `${index}`,
										id: `${index}`,
										spaceId: `${index}`,
									},
								},
								sys: {
									environmentId: `${index}`,
									id: `${index}`,
									spaceId: `${index}`,
								},
								contentfulMetadata: {
									tags: [],
									concepts: [],
								},
							})),
							limit: provider.providerImages.length,
							skip: 0,
							total: provider.providerImages.length,
						}}
					/>
				</Box>
			)}
		</Box>
	);
}
